import { Form } from 'antd';
import { getGlobalSettings } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { ContractInfo } from 'entities/Contract';
import { PaymentMethod, PaymentMethodSetting, PaymentProcessor } from 'entities/Payment';
import { getLoggedUserData } from 'entities/User';
import { WarehouseForBooking } from 'entities/Warehouse';
import { GratitudeModalType, useOpenGratitudeModal } from 'features/GratitudeModal';
import { PriceDetailsTable } from 'features/PriceDetailsTable';
import { ReserveBox } from 'features/ReserveBox';
import { STARTED_BOOKING_DATA_KEY } from 'pages/BookingPage/utils/constants';
import { FC, memo, ReactNode, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { CollapseRadioButtonGroup } from 'shared/ui/CollapseRadioButtonGroup';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { BookAndPayButton } from './BookAndPayButton';

interface PaymentFormProps {
  contractInfo: ContractInfo;
  boxInfo: Nullable<Box>;
  warehouseInfo: Nullable<WarehouseForBooking>;
  onCompleteStep: () => void;
}

export const PaymentForm: FC<PaymentFormProps> = memo((props) => {
  const { boxInfo, warehouseInfo, contractInfo, onCompleteStep } = props;

  const { t } = useAppTranslation(['booking', 'common']);
  const navigate = useNavigate();

  const { openGratitudeModal } = useOpenGratitudeModal();

  const [termsAgreement, setTermsAgreement] = useState<boolean>(false);
  const [isAutopaymentEnabled, setAutopaymentEnabled] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.ONLINE_PAYMENT);

  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);

  useLayoutEffect(() => {
    scrollToElement();
  }, [scrollToElement]);

  useEffect(() => {
    const watchAutoPaymentStatus = (): void => {
      switch (paymentMethod) {
        case PaymentMethod.ONLINE_PAYMENT:
          setAutopaymentEnabled(true);
          break;
        case PaymentMethod.BANK_TRANSFER:
          setAutopaymentEnabled(false);
          break;
        case PaymentMethod.OTHER:
          setAutopaymentEnabled(false);
          break;
      }
    };

    watchAutoPaymentStatus();
  }, [paymentMethod]);

  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUserData = useAppSelector(getLoggedUserData);

  /* const isPrePaymentApplied = contractInfo.payForEntirePeriod; */ // ---> This will be changed

  const toggleAutopayment = useCallback((): void => {
    setAutopaymentEnabled((prevState) => !prevState);
  }, []);

  const getPaymentMethodOption = (paymentMethodSetting: PaymentMethodSetting): { value: string; label: ReactNode; content: ReactNode } => {
    const detaultOtherOption = {
      label: t('Other'),
      value: PaymentProcessor.OTHER,
      content: t(
        'You will find the invoice for payment in your personal account. Please note that you cannot use the box until you have paid the first bill. Please pay your first bill before your move-in date',
      ),
    };

    const mapper: Partial<Record<PaymentMethod, { value: string; label: ReactNode; content: ReactNode }>> = {
      [PaymentMethod.ONLINE_PAYMENT]: {
        label: (
          <div className="flex flex-col items-start justify-between w-full desktop:flex-row desktop:items-center">
            <span>{t('Online payment')}</span>
            <div className="flex flex-wrap items-center gap-2">
              {paymentMethodSetting?.imageUrl && <img src={paymentMethodSetting.imageUrl} className="max-h-[100px] ml-5" />}
            </div>
          </div>
        ),
        value: PaymentMethod.ONLINE_PAYMENT,
        content: (
          <div>
            <div className="mb-4 text-primaryLight">
              {t('We accept all major credit and debit cards, including Visa, Mastercard, American Express, GooglePay and ApplePay.')}
              <br />
            </div>
            {paymentMethodSetting.isAutoPaymentAllowed && (
              <>
                <div className="font-semibold text-lg">{t('Auto pay')}</div>
                <div className="mb-2">
                  {t('Turn on the automatic payment feature so you don`t miss payments. You can turn it off at any time!')}
                </div>
                <Checkbox
                  name="isAutopayment"
                  checked={isAutopaymentEnabled}
                  onChange={toggleAutopayment} /* disabled={isPrePaymentApplied} */
                >
                  {t('Allow autopayment')}
                </Checkbox>
              </>
            )}
          </div>
        ),
      },
      [PaymentMethod.BANK_TRANSFER]: {
        label: t('Bank transfer'),
        value: PaymentMethod.BANK_TRANSFER,
        content: t(
          'You will find the invoice for payment in your personal account. Please note that you cannot use the box until you have paid the first bill. Please pay your first bill before your move-in date',
        ),
      },
      [PaymentMethod.OTHER]: {
        label: t('Other (You can pay later using one of the available payment methods)'),
        value: PaymentMethod.OTHER,
        content: t(
          'You will find the invoice for payment in your personal account. Please note that you cannot use the box until you have paid the first bill. Please pay your first bill before your move-in date',
        ),
      },
    };

    return mapper[paymentMethodSetting.name] || detaultOtherOption;
  };

  const paymentMethodsOptions = globalSettings?.publicPaymentMethods
    .map((paymentMethodSetting: PaymentMethodSetting) => getPaymentMethodOption(paymentMethodSetting))
    .filter(Boolean);

  const toggleTermsAgreement = useCallback((): void => {
    setTermsAgreement((prevState) => !prevState);
  }, []);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id', // TODO ADD ID FROM MARCUS !!!
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [handleOpenGratitudeModal, navigate]);

  return (
    <div className="space-y-6">
      <div className="bg-secondaryLight rounded-lg p-6">
        <PriceDetailsTable boxInfo={boxInfo} contractInfo={contractInfo} />
      </div>
      <div className="bg-secondaryLight rounded-lg p-6">
        <div ref={scrollRef} className="font-semibold text-3xl mb-6">
          {t('Select payment method')}
        </div>
        <Form>
          <div className="mb-4">
            <CollapseRadioButtonGroup
              options={paymentMethodsOptions || []}
              selectedOption={paymentMethod}
              setSelectedOption={setPaymentMethod}
            />
          </div>
          <Checkbox name="termsAgreement" requiredMark checked={termsAgreement} onChange={toggleTermsAgreement}>
            {t('I have read the following additions to the contract and accept them in full:')}
          </Checkbox>
          <div className="flex flex-col mt-2 mb-6">
            {globalSettings?.bookAndPayDocuments.map((doc) => (
              <Link key={doc.link} className="text-accent text-sm font-normal" to={doc.link} target="_blank">
                • {t(doc.docName)}
              </Link>
            ))}
          </div>

          <div className="flex space-x-3">
            <ReserveBox
              action={
                <Button
                  containerClassName="w-full"
                  size="large"
                  theme="secondary"
                  isDisabled={!termsAgreement}
                  description={
                    <Trans
                      t={t}
                      i18nKey="Reserve for <0>3 days free</0>. <1>No credit card needed!</1>"
                      components={[
                        <span key="0" className="text-accent text-xs font-medium" />,
                        <div key="1" className="text-xs font-normal" />,
                      ]}
                    />
                  }
                >
                  {t('Reserve')}
                </Button>
              }
              boxId={boxInfo?.boxId}
              contractOptions={contractInfo}
              warehouseId={warehouseInfo?.warehouseId}
              onSuccess={onReservationSuccess}
              userId={loggedUserData?.userId}
            />

            <BookAndPayButton
              isDisabled={!termsAgreement}
              loggedUserData={loggedUserData}
              contractInfo={contractInfo}
              boxInfo={boxInfo}
              warehouseInfo={warehouseInfo}
              globalSettings={globalSettings}
              isAutopaymentEnabled={isAutopaymentEnabled}
              paymentMethod={paymentMethod}
              onCompleteStep={onCompleteStep}
            />
          </div>
        </Form>
      </div>
    </div>
  );
});
